import { api } from "@/shared/api";

// FSD entities/dh-profile — 形象/声纹档案(真人生物特征资产:授权入库后全公司共用、录音克隆须本人、
// 跨公司隔离、删除仅本人或管理员)+ 预置安全资产。
// packs W2 从 features/digital-human/api/digitalHumanService 抽出:人物库(features/characters)
// 老板 IP 流要走 enroll→升格,FSD features 互不 import,跨 feature 业务能力下沉 entities。
// 端点契约原样(base /api/v1/digital-human),digitalHumanService 仍以同名方法委托(调用面零改动)。

/** 声纹档案(B1)。voice_id 不下发前端(治理:只暴露语义 label / 状态)。 */
export interface VoiceProfileDto {
  id: number;
  label: string;
  /** upload(传文件)/ record(录音)。 */
  source: string;
  status: string;
  /** 样本试听 URL(presign,owner 可听)。 */
  sampleUrl?: string | null;
  createdAt?: string | null;
  /**
   * 声纹引擎(1006):qwen_audio = 新模型(方言/多语言/演绎生效);cosyvoice = 旧版声纹。
   * 老后端可能不下发 —— 消费面不据它做判断,判锁只看 `supportsTuning`。
   */
  provider?: "qwen_audio" | "cosyvoice" | null;
  /**
   * 方言/多语言/演绎是否对这条声纹生效(1006)。★缺省(老后端)按 true —— 不误锁;
   * 只有显式 false 才算「旧版声纹」。统一走 {@link voiceProfileSupportsTuning}。
   */
  supportsTuning?: boolean | null;
}

/**
 * 声纹录制日期短标(1006,「声纹太多显示不全」:同名声纹靠日期区分)→ "MM-DD"。
 * 优先按字面截 ISO 日期段(不经时区换算,与后端存的日子一致);认不出 → null(不编日期)。
 */
export function voiceProfileDateLabel(createdAt: string | null | undefined): string | null {
  if (!createdAt) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(createdAt.trim());
  return m ? `${m[2]}-${m[3]}` : null;
}

/** 声纹是否支持三件套。缺省/null 按 true(老后端不下发时不误锁),只认显式 false。 */
export const voiceProfileSupportsTuning = (
  p: Pick<VoiceProfileDto, "supportsTuning"> | null | undefined
) => p?.supportsTuning !== false;

/** 我的声纹列表 + 配额卡。 */
export interface VoiceProfileListView {
  profiles: VoiceProfileDto[];
  used: number;
  limit?: number | null;
}

/** 预置形象(B2,平台共享安全资产)。objectKey 不下发,只 label + 缩略图 presign。 */
export interface PresetAvatarDto {
  id: string;
  label: string;
  thumbnailUrl: string;
  /** C2 场景分类:lecturer 讲师 / business 商务 / retail 门店 / medical 医疗 / livestream 直播。 */
  category?: string | null;
}

/** 预置音色(C2,平台策展)。sampleUrl=null 表示该音色暂无样音 → 不渲染试听(不做假试听)。 */
export interface PresetVoiceDto {
  id: string;
  label: string;
  /** 用途:broadcast 播报 / social 种草 / explain 讲解 / emotion 情感 / cartoon 卡通。 */
  usage?: string | null;
  sampleUrl?: string | null;
}

/** 形象(#10,可复用形象库;授权入库后全公司共用,删除仅本人或管理员)。objectKey 不下发,只 label + 缩略图 presign。 */
export interface AvatarProfileDto {
  id: number;
  label: string;
  /** photo(照片档)/ video(视频档,出片自动走视频驱动管线)。 */
  kind?: "photo" | "video";
  /**
   * 「挂到人物」显式关联(P0.6 挂账车 L,仅对 kind=video 形象有意义):该形象挂到哪个人物,
   * 未挂接为 null/undefined。真人出镜视频身份解析(后端 RealPersonOwnRefs.selectedVideoAvatar)
   * 按此优先,查不到才退回 principal 兜底。
   */
  characterId?: number | null;
  /** upload(现阶段唯一来源)。 */
  source: string;
  status: string;
  /** 形象素材 URL(presign,owner 可见;video 为源视频地址)。 */
  thumbnailUrl?: string | null;
  createdAt?: string | null;
}

/** 我的形象列表 + 配额卡(#10)。 */
export interface AvatarProfileListView {
  profiles: AvatarProfileDto[];
  used: number;
  limit?: number | null;
}

const BASE = "/digital-human";

export const dhProfileService = {
  /**
   * 读租户数字人治理开关(真人照片上传是否允许)。
   * ★2026-08-11(0810 反馈玉玉):不再打 /tenant/settings —— 那是管理员端点
   * (TENANT_ADMIN/MANAGER),普通成员进分身舱创建页每次必 403。改打成员可读窄面
   * /tenant/settings/dh-gate(只回这一个布尔,登录即可读)。
   */
  getTenantGate: () => api.get<{ dhAllowRealPhoto?: boolean }>("/tenant/settings/dh-gate"),

  /** 我的声纹 + 配额(B1)。 */
  listVoiceProfiles: () => api.get<VoiceProfileListView>(`${BASE}/voice-profiles`),

  /**
   * 复刻声纹:multipart 样本 + label + 授权勾选,返新档案。
   * B5 即录即证:source=record + consentTextVersion(现场朗读授权文本版本号,录音即存证)。
   */
  enrollVoiceProfile: (
    file: File,
    label: string,
    consent: boolean,
    opts?: { source?: "upload" | "record"; consentTextVersion?: string }
  ): Promise<VoiceProfileDto> => {
    const form = new FormData();
    form.append("file", file);
    if (label) form.append("label", label);
    form.append("consentAcknowledged", String(consent));
    if (opts?.source) form.append("source", opts.source);
    if (opts?.consentTextVersion) form.append("consentTextVersion", opts.consentTextVersion);
    return api.post<VoiceProfileDto>(`${BASE}/voice-profiles`, form);
  },

  /** 删声纹(owner 删自己 / 管理员强制下架);传导释放 DashScope slot。 */
  deleteVoiceProfile: (id: number) => api.delete<void>(`${BASE}/voice-profiles/${id}`),

  /** 预置形象库(B2,平台共享安全资产,只读)。 */
  listPresetAvatars: () => api.get<PresetAvatarDto[]>(`${BASE}/preset-avatars`),

  /** 预置音色库(C2,平台共享安全资产,只读;带试听样音 presign)。 */
  listPresetVoices: () => api.get<PresetVoiceDto[]>(`${BASE}/preset-voices`),

  /*
   * ★形象四口(listAvatarProfiles / enrollAvatarProfile / deleteAvatarProfile /
   *   setAvatarCharacterLink)整组退役(0915 归一批 B):形象表并入 xb_pack_material。
   *
   * 替代口:素材挂在人物下,走 `characterService.uploadMaterial/deleteMaterial`
   * (人物详情页「这个人长什么样」那一块)。「挂到人物」这个动作本身也没有了 ——
   * 素材从落库那一刻起就属于某个人物,不存在"先有一条无主素材、再挂过去"的中间态,
   * V21 那条反向挂接列随之退役。
   */
};
